// Iter 83.5 / Phase E4 Sprint 5 — Asset Handover certificate print.
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

const fmtMoney = (n) =>
    `₹ ${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function AssetHandoverPrint() {
    const { assignmentId } = useParams();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const { data: d } = await api.get(`/print/asset-handover/${assignmentId}`);
                setData(d);
            } catch (err) {
                setError(formatApiError(err, "Failed to load handover certificate"));
                toast.error(formatApiError(err, "Load failed"));
            }
        })();
    }, [assignmentId]);

    if (error) return <div className="p-12 text-center text-rose-600" data-testid="asset-handover-error">{error}</div>;
    if (!data) return <div className="p-12 text-center text-slate-400">Loading…</div>;
    const { assignment, asset, employee, category, company, generated_at } = data;

    return (
        <div className="bg-white min-h-screen p-12 font-serif" data-testid="asset-handover-print">
            <style>{`@media print { @page { size: A4; margin: 12mm; } body { -webkit-print-color-adjust: exact; } }`}</style>

            <header className="border-b-4 border-slate-900 pb-4 mb-6 flex justify-between">
                <div>
                    <div className="text-xs uppercase tracking-widest text-slate-600">{company.legal_name}</div>
                    <h1 className="text-3xl font-bold mt-1">Asset Handover Certificate</h1>
                    <div className="text-sm text-slate-600 mt-1">Iter 83.5 / Sprint 5 · Custody Acknowledgement</div>
                </div>
                <div className="text-right text-xs">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">Date</div>
                    <div className="font-mono">{(assignment.assigned_at || generated_at || "").slice(0, 10)}</div>
                </div>
            </header>

            <p className="text-sm mb-4">
                This certificate confirms that the asset described below has been handed over to{" "}
                <strong>{employee?.full_name}</strong> ({employee?.employee_code}) of {employee?.department || "—"} for
                official use, with effect from{" "}
                <strong>{(assignment.assigned_at || "").slice(0, 10)}</strong>.
            </p>

            <section className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm border-y border-slate-300 py-4 mb-4">
                <Pair label="Asset Tag" value={asset?.asset_tag} mono />
                <Pair label="Category" value={category?.name || asset?.category_name_snapshot} />
                <Pair label="Make / Model" value={`${asset?.make || ""} ${asset?.model || ""}`.trim()} />
                <Pair label="Serial #" value={asset?.serial_number || "—"} mono />
                <Pair label="Purchase Date" value={asset?.purchase_date || "—"} />
                <Pair label="Current Depreciated Value" value={fmtMoney(asset?.depreciated_value)} />
                <Pair label="Assigned Location" value={assignment.location || "—"} />
                <Pair label="Expected Return" value={assignment.expected_return_on || "On separation"} />
                <Pair label="Assignment ID" value={assignment.id} mono />
                <Pair label="Status" value={assignment.status} />
            </section>

            <section className="text-sm mb-4 leading-relaxed">
                <h2 className="font-bold mb-2">Custodian Obligations</h2>
                <ol className="list-decimal pl-5 space-y-1 text-xs">
                    <li>I will use the asset solely for official duties and exercise reasonable care.</li>
                    <li>I will not transfer, lend, or modify the asset without IT approval.</li>
                    <li>Loss, theft, or damage must be reported to IT immediately and may be subject to recovery per policy.</li>
                    <li>I will return the asset on the expected return date OR on separation, whichever is earlier.</li>
                    <li>On return, the asset will be inspected. Outcome may be Good / Damaged / Lost — and any damage may attract a recovery (D18) per the asset return state machine.</li>
                </ol>
            </section>

            <section className="mt-12 grid grid-cols-2 gap-12">
                <div>
                    <div className="border-t border-slate-700 pt-1 text-xs">Employee Signature · Date</div>
                    <div className="text-[10px] text-slate-500 mt-1">{employee?.full_name}</div>
                </div>
                <div>
                    <div className="border-t border-slate-700 pt-1 text-xs">IT / HR Custodian</div>
                    <div className="text-[10px] text-slate-500 mt-1">For {company.legal_name}</div>
                    <div className="text-[10px] text-slate-500 mt-1">
                        Assigned by {assignment.assigned_by_email || assignment.assigned_by_id || "—"}
                    </div>
                </div>
            </section>
        </div>
    );
}

function Pair({ label, value, mono }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
            <div className={mono ? "font-mono" : ""}>{value || "—"}</div>
        </div>
    );
}
